package br.com.htms.inspeccaocampo.data

enum class AcompAmbAba(val id: String, val rotulo: String, val monthly: Boolean, val informative: Boolean = false) {
    PROCEDIMENTOS_GERAIS("PROCEDIMENTOS_GERAIS", "Procedimentos gerais", false, true),
    DOCUMENTOS_SOLICITADOS("DOCUMENTOS_SOLICITADOS", "Documentos solicitados", false, true),
    RECURSOS_HIDRICOS("RECURSOS_HIDRICOS", "Recursos hídricos", true),
    COMBUSTIVEL("COMBUSTIVEL", "Combustível", true),
    EFLUENTES("EFLUENTES", "Efluentes", true),
    RESIDUOS_ARMAZENAMENTO("RESIDUOS_ARMAZENAMENTO", "Resíduos sólidos — armazenamento", false),
    RESIDUOS_GERACAO("RESIDUOS_GERACAO", "Resíduos sólidos — geração", true),
    SUPRESSAO_VEGETAL("SUPRESSAO_VEGETAL", "Supressão vegetal", false),
    PRECIPITACAO("PRECIPITACAO", "Precipitação", true);

    companion object {
        fun fromId(raw: String?): AcompAmbAba =
            entries.find { it.id.equals(raw, ignoreCase = true) } ?: PROCEDIMENTOS_GERAIS
    }
}

data class AcompAmbMonthPayload(
    val values: Map<String, String> = emptyMap(),
    val fotos: Map<String, List<String>> = emptyMap()
) {
    fun get(key: String): String = values[key].orEmpty()

    fun withValue(key: String, value: String): AcompAmbMonthPayload =
        copy(values = values + (key to value))

    fun fotosDoSlot(slot: String): List<String> = fotos[slot].orEmpty()

    fun withFotosSlot(slot: String, keys: List<String>): AcompAmbMonthPayload {
        val next = fotos.toMutableMap()
        if (keys.isEmpty()) next.remove(slot) else next[slot] = keys
        return copy(fotos = next)
    }
}

data class AcompAmbMonthRow(
    val id: String? = null,
    val aba: AcompAmbAba = AcompAmbAba.PROCEDIMENTOS_GERAIS,
    val mes: Int = 0,
    val payload: AcompAmbMonthPayload = AcompAmbMonthPayload(),
    val updatedAt: Long? = null
)

data class AcompAmbDossier(
    val id: String,
    val userId: String? = null,
    val username: String? = null,
    val obra: String,
    val ano: Int,
    val createdAt: Long? = null,
    val updatedAt: Long? = null,
    val months: List<AcompAmbMonthRow> = emptyList()
)

data class FormAcompanhamentoAmbiental(
    val dossierId: String? = null,
    val obra: String = "",
    val ano: Int = 2026,
    val aba: AcompAmbAba = AcompAmbAba.PROCEDIMENTOS_GERAIS,
    val mes: Int = 1,
    val payload: AcompAmbMonthPayload = AcompAmbMonthPayload(),
    val rowsCache: List<AcompAmbMonthRow> = emptyList(),
    val dirty: Boolean = false,
    val readOnly: Boolean = false
) {
    fun effectiveMes(): Int = if (AcompAmbSchema.monthly(aba)) mes else 0

    fun payloadFor(abaSel: AcompAmbAba, mesSel: Int): AcompAmbMonthPayload =
        rowsCache.find { it.aba == abaSel && it.mes == mesSel }?.payload
            ?: if (aba == abaSel && effectiveMes() == mesSel) payload else AcompAmbMonthPayload()
}

object AcompAmbMeses {
    val rotulos = RemMeses.rotulos
    fun rotulo(mes: Int): String = RemMeses.rotulo(mes)
}

data class AcompAmbFieldDef(
    val key: String,
    val label: String,
    val type: String = "text"
)

data class AcompAmbGridRowDef(
    val key: String,
    val label: String
)

data class AcompAmbInfoBlock(
    val title: String,
    val lines: List<String>
)

data class AcompAmbPhotoSlotDef(
    val key: String,
    val label: String,
    val maxPhotos: Int = 4
)

data class AcompAmbAbaSchema(
    val aba: AcompAmbAba,
    val fields: List<AcompAmbFieldDef> = emptyList(),
    val gridRows: List<AcompAmbGridRowDef> = emptyList(),
    val infoBlocks: List<AcompAmbInfoBlock> = emptyList(),
    val photoSlots: List<AcompAmbPhotoSlotDef> = emptyList()
)

object AcompAmbSchema {
    private val schemas: Map<AcompAmbAba, AcompAmbAbaSchema> = mapOf(
        AcompAmbAba.PROCEDIMENTOS_GERAIS to AcompAmbAbaSchema(
            aba = AcompAmbAba.PROCEDIMENTOS_GERAIS,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Gestão Ambiental HTMS Group",
                    lines = listOf(
                        "Requisitos mínimos do sistema de gestão ambiental.",
                        "Se houver exigências extras em Licenças, autorizações ou Planos Ambientais, outros documentos poderão ser solicitados.",
                        "Os documentos desta planilha são específicos das tarefas e não substituem os previstos em contrato, leis e normas.",
                        "Documentos com validade devem estar em dia na pasta da obra e no canteiro."
                    )
                ),
                AcompAmbInfoBlock(
                    title = "Uso proibido",
                    lines = listOf(
                        "Aparelhos com gases nocivos à camada de ozônio (CFCs, gás freon)",
                        "Asbesto / Amianto",
                        "Óleo Ascarel",
                        "Óleo com PCB"
                    )
                )
            )
        ),
        AcompAmbAba.DOCUMENTOS_SOLICITADOS to AcompAmbAbaSchema(
            aba = AcompAmbAba.DOCUMENTOS_SOLICITADOS,
            fields = listOf(
                AcompAmbFieldDef("empresa", "Empresa"),
                AcompAmbFieldDef("projeto", "Projeto"),
                AcompAmbFieldDef("engResponsavel", "Eng. responsável")
            ),
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Documentos solicitados",
                    lines = listOf(
                        "Aba orientativa da Qualificação Ambiental: mantenha evidências na pasta da obra (QSMS/Meio Ambiente) e respeite o prazo de envio da informação + evidência.",
                        "EPC: Licença ambiental/isenção; CTF APP/AIDA; Procedimento de Supressão Vegetal; Licença de Motosserras (IBAMA); envio mensal dos MTRs.",
                        "Supressão vegetal: CTF APP/AIDA; Licença Ambiental; Licença de Motosserra (IBAMA).",
                        "Resíduos sólidos: CTF APP/AIDA; Licenças de transporte e destinação/tratamento; controle de geração (planilha modelo).",
                        "Efluentes: CTF APP/AIDA; Licenças de transporte e destinação/tratamento; controle de geração.",
                        "Recursos hídricos: CTF APP/AIDA; Licença da distribuidora (caminhão pipa); evidenciar contratação e registrar consumo (NF + planilha).",
                        "Combustível: Licença de transporte de produto perigoso; CTF APP/AIDA; PGRS de resíduo perigoso; controle de consumo (NF + planilha)."
                    )
                )
            )
        ),
        AcompAmbAba.RECURSOS_HIDRICOS to AcompAmbAbaSchema(
            aba = AcompAmbAba.RECURSOS_HIDRICOS,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Recursos hídricos",
                    lines = listOf(
                        "Informe o consumo mensal (m³) por fonte: água mineral, caminhão pipa e hidrômetro do canteiro.",
                        "Evidencie a contratação do caminhão pipa e anexe NF/controle de consumo na pasta da obra, conforme a aba Documentos solicitados."
                    )
                )
            ),
            gridRows = listOf(
                AcompAmbGridRowDef("aguaMineral", "Água mineral (consumo) — m³"),
                AcompAmbGridRowDef("caminhaoPipa", "Caminhão pipa — m³"),
                AcompAmbGridRowDef("hidrometro", "Hidrômetro do canteiro — m³")
            )
        ),
        AcompAmbAba.COMBUSTIVEL to AcompAmbAbaSchema(
            aba = AcompAmbAba.COMBUSTIVEL,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Combustível",
                    lines = listOf(
                        "Informe o consumo mensal (m³). Para converter litros em m³, divida por 1.000.",
                        "Preencha os detalhes de abastecimento (in loco, forma, kit de emergência e local).",
                        "Registro fotográfico com coordenadas: bacia de contenção + kit de mitigação."
                    )
                )
            ),
            gridRows = listOf(
                AcompAmbGridRowDef("dieselS10", "Diesel S10 — m³"),
                AcompAmbGridRowDef("gasolinaAditivada", "Gasolina aditivada — m³"),
                AcompAmbGridRowDef("gasolinaComum", "Gasolina comum — m³"),
                AcompAmbGridRowDef("aditivoRadiador", "Aditivo radiador Fresh — m³"),
                AcompAmbGridRowDef("orbiAditivo", "Orbi aditivo — m³")
            ),
            fields = listOf(
                AcompAmbFieldDef("abastecidoInLoco", "Abastecido in loco? (Sim/Não)"),
                AcompAmbFieldDef("comoAbastecimento", "Como é feito o abastecimento?"),
                AcompAmbFieldDef("kitEmergencia", "Kit de emergência? (Sim/Não)"),
                AcompAmbFieldDef("localArmazenamento", "Local de armazenamento/abastecimento")
            ),
            photoSlots = listOf(
                AcompAmbPhotoSlotDef("bacias_kit", "Registro fotográfico — Bacia de contenção + Kit de mitigação (com coordenadas)")
            )
        ),
        AcompAmbAba.EFLUENTES to AcompAmbAbaSchema(
            aba = AcompAmbAba.EFLUENTES,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Efluentes",
                    lines = listOf(
                        "Informe a geração mensal (m³) de banheiro químico e banheiro container.",
                        "Anexe evidência fotográfica da instalação do biodigestor."
                    )
                )
            ),
            gridRows = listOf(
                AcompAmbGridRowDef("banheiroQuimico", "Banheiro químico — m³"),
                AcompAmbGridRowDef("banheiroContainer", "Banheiro container — m³")
            ),
            photoSlots = listOf(
                AcompAmbPhotoSlotDef("biodigestor", "Evidência da instalação do biodigestor (fotos)")
            )
        ),
        AcompAmbAba.RESIDUOS_ARMAZENAMENTO to AcompAmbAbaSchema(
            aba = AcompAmbAba.RESIDUOS_ARMAZENAMENTO,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Resíduos sólidos (armazenamento)",
                    lines = listOf(
                        "Descreva acondicionamento, armazenamento e destinação de cada tipo de resíduo.",
                        "Registro fotográfico com coordenadas: forma de acondicionamento, local de armazenamento e destinação.",
                        "Anexe MTRs e a licença de operação do destino; mantenha PGRS/PGRCC atualizados na pasta da obra (impresso ou QR)."
                    )
                )
            ),
            fields = listOf(
                AcompAmbFieldDef("papel_acond", "Papel — acondicionamento"),
                AcompAmbFieldDef("papel_armazen", "Papel — armazenamento"),
                AcompAmbFieldDef("papel_destin", "Papel — destinação"),
                AcompAmbFieldDef("plastico_acond", "Plástico — acondicionamento"),
                AcompAmbFieldDef("plastico_armazen", "Plástico — armazenamento"),
                AcompAmbFieldDef("plastico_destin", "Plástico — destinação"),
                AcompAmbFieldDef("vidro_acond", "Vidro — acondicionamento"),
                AcompAmbFieldDef("vidro_armazen", "Vidro — armazenamento"),
                AcompAmbFieldDef("vidro_destin", "Vidro — destinação"),
                AcompAmbFieldDef("metal_acond", "Metal — acondicionamento"),
                AcompAmbFieldDef("metal_armazen", "Metal — armazenamento"),
                AcompAmbFieldDef("metal_destin", "Metal — destinação"),
                AcompAmbFieldDef("madeira_acond", "Madeira — acondicionamento"),
                AcompAmbFieldDef("madeira_armazen", "Madeira — armazenamento"),
                AcompAmbFieldDef("madeira_destin", "Madeira — destinação"),
                AcompAmbFieldDef("perigosos_acond", "Perigosos — acondicionamento"),
                AcompAmbFieldDef("perigosos_armazen", "Perigosos — armazenamento"),
                AcompAmbFieldDef("perigosos_destin", "Perigosos — destinação"),
                AcompAmbFieldDef("organico_acond", "Orgânico — acondicionamento"),
                AcompAmbFieldDef("organico_armazen", "Orgânico — armazenamento"),
                AcompAmbFieldDef("organico_destin", "Orgânico — destinação"),
                AcompAmbFieldDef("isopor_acond", "Isopor — acondicionamento"),
                AcompAmbFieldDef("isopor_armazen", "Isopor — armazenamento"),
                AcompAmbFieldDef("isopor_destin", "Isopor — destinação"),
                AcompAmbFieldDef("descricaoGeral", "Descrição geral")
            ),
            photoSlots = listOf(
                AcompAmbPhotoSlotDef("forma_acondicionamento", "Registro fotográfico — Forma de acondicionamento (com coordenadas)"),
                AcompAmbPhotoSlotDef("local_armazenamento", "Registro fotográfico — Local de armazenamento (com coordenadas)"),
                AcompAmbPhotoSlotDef("destinacao", "Registro fotográfico — Destinação (com coordenadas)")
            )
        ),
        AcompAmbAba.RESIDUOS_GERACAO to AcompAmbAbaSchema(
            aba = AcompAmbAba.RESIDUOS_GERACAO,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Resíduos sólidos (geração)",
                    lines = listOf(
                        "Informe a geração mensal (kg) por tipo de resíduo.",
                        "Documentos a anexar na pasta da obra: LO do destino + LO do transporte + MTR de cada resíduo.",
                        "No Excel oficial há também blocos de destinação (reciclagem, aterro, compostagem e outras opções) — registre evidências na pasta da obra."
                    )
                )
            ),
            gridRows = listOf(
                AcompAmbGridRowDef("papel", "Papel (kg)"),
                AcompAmbGridRowDef("plastico", "Plástico (kg)"),
                AcompAmbGridRowDef("papelao", "Papelão (kg)"),
                AcompAmbGridRowDef("metal", "Metal (kg)"),
                AcompAmbGridRowDef("madeira", "Madeira (kg)"),
                AcompAmbGridRowDef("perigosos", "Perigosos (kg)"),
                AcompAmbGridRowDef("organico", "Orgânico (kg)"),
                AcompAmbGridRowDef("isopor", "Isopor (kg)")
            )
        ),
        AcompAmbAba.SUPRESSAO_VEGETAL to AcompAmbAbaSchema(
            aba = AcompAmbAba.SUPRESSAO_VEGETAL,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Supressão vegetal",
                    lines = listOf(
                        "Preencha data, maquinário, local de armazenamento, destinação do material lenhoso, empresa e área (ha).",
                        "Registro fotográfico com coordenadas: atividade de supressão e local de armazenamento final (dentro da área do proprietário, material organizado).",
                        "Anexar à planilha: CTF IBAMA e Licença Ambiental da empresa contratada + Licença de Motosserra (IBAMA)."
                    )
                )
            ),
            fields = listOf(
                AcompAmbFieldDef("dataAtividade", "Data da atividade"),
                AcompAmbFieldDef("maquinario", "Maquinário utilizado"),
                AcompAmbFieldDef("localArmazenamento", "Local de armazenamento"),
                AcompAmbFieldDef("destinacaoLenhoso", "Destinação do material lenhoso"),
                AcompAmbFieldDef("empresaContratada", "Empresa contratada"),
                AcompAmbFieldDef("areaSuprimidaHa", "Área suprimida (ha)", type = "number"),
                AcompAmbFieldDef("descricao", "Descrição")
            ),
            photoSlots = listOf(
                AcompAmbPhotoSlotDef("atividade_supressao", "Registro fotográfico — Atividade de supressão (com coordenadas)"),
                AcompAmbPhotoSlotDef("local_armazenamento_sup", "Registro fotográfico — Local de armazenamento (com coordenadas)")
            )
        ),
        AcompAmbAba.PRECIPITACAO to AcompAmbAbaSchema(
            aba = AcompAmbAba.PRECIPITACAO,
            infoBlocks = listOf(
                AcompAmbInfoBlock(
                    title = "Orientação — Precipitação",
                    lines = listOf(
                        "Informe a precipitação mensal (mm) de cada pluviômetro instalado na obra."
                    )
                )
            ),
            gridRows = listOf(
                AcompAmbGridRowDef("pluviometro1", "Pluviômetro 1 (mm)"),
                AcompAmbGridRowDef("pluviometro2", "Pluviômetro 2 (mm)"),
                AcompAmbGridRowDef("pluviometro3", "Pluviômetro 3 (mm)"),
                AcompAmbGridRowDef("pluviometro4", "Pluviômetro 4 (mm)"),
                AcompAmbGridRowDef("pluviometro5", "Pluviômetro 5 (mm)")
            )
        )
    )

    fun schema(aba: AcompAmbAba): AcompAmbAbaSchema = schemas[aba] ?: AcompAmbAbaSchema(aba)

    fun monthly(aba: AcompAmbAba): Boolean = aba.monthly

    fun informative(aba: AcompAmbAba): Boolean = aba.informative

    fun allPhotoSlotKeys(): List<String> =
        schemas.values.flatMap { s -> s.photoSlots.map { it.key } }.distinct()

    val abas: List<AcompAmbAba> = AcompAmbAba.entries
}
